import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { api, type Rule, type RuleRequest, type Tier } from '../lib/api';
import { useAsync } from '../lib/useAsync';

const EVENTS = ['participation.recorded', 'fan.joined'];
const blank: RuleRequest = { name: '', eventType: 'participation.recorded', activityType: 'poll', kind: 'fixed', amount: 10, enabled: true };

/** Rules and the tier ladder: the economy as data. The worker applies rules; tiers are recomputed on every balance change. */
export function Economy() {
  const { orgId } = useParams();
  const org = useAsync(() => api.orgs.get(orgId!), [orgId]);
  const rules = useAsync(() => api.rules.list(orgId!), [orgId]);
  const tiers = useAsync(() => api.tiers.list(orgId!), [orgId]);
  const [form, setForm] = useState<RuleRequest>(blank);
  const [editing, setEditing] = useState<Rule | null>(null);
  const [ladder, setLadder] = useState<{ name: string; minPoints: number }[]>([]);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => { if (tiers.data) setLadder(tiers.data.map((t: Tier) => ({ name: t.name, minPoints: t.minPoints }))); }, [tiers.data]);

  async function saveRule(e: FormEvent) {
    e.preventDefault(); setError(''); setNote('');
    try {
      const body: RuleRequest = { ...form, activityType: form.eventType === 'participation.recorded' ? (form.activityType ?? null) : null };
      if (editing) await api.rules.update(orgId!, editing.id, body); else await api.rules.create(orgId!, body);
      setForm(blank); setEditing(null); rules.reload();
    } catch (err) { setError((err as Error).message); }
  }
  async function toggle(r: Rule) {
    try { await api.rules.update(orgId!, r.id, { ...r, enabled: !r.enabled }); rules.reload(); } catch (err) { setError((err as Error).message); }
  }
  async function remove(r: Rule) {
    try { await api.rules.remove(orgId!, r.id); rules.reload(); } catch (err) { setError((err as Error).message); }
  }
  async function saveLadder(e: FormEvent) {
    e.preventDefault(); setError(''); setNote('');
    try { await api.tiers.replace(orgId!, ladder); tiers.reload(); setNote('Ladder saved. Fans move tiers the next time they earn points.'); } catch (err) { setError((err as Error).message); }
  }

  return (
    <>
      <h1>{org.data?.name ?? '…'} <span className="muted" style={{ fontWeight: 400 }}>· Points & tiers</span></h1>
      {error && <p className="err">{error}</p>}
      {note && <p className="ok">{note}</p>}

      <h2>Rules</h2>
      <p className="muted">What earns points. Each rule fires at most once per source (a vote, a quiz attempt, a join), so redelivery never double-pays.</p>
      {rules.data && (
        <table>
          <thead><tr><th>Rule</th><th>When</th><th>Points</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rules.data.map((r) => (
              <tr key={r.id}>
                <td><b>{r.name}</b></td>
                <td className="muted">{r.eventType}{r.activityType ? ` · ${r.activityType}` : ''}</td>
                <td>{r.kind === 'per_correct' ? `${r.amount} per correct answer` : r.kind === 'from_result' ? "the quiz's own points" : r.amount}</td>
                <td><span className={`pill ${r.enabled ? 'published' : 'closed'}`}>{r.enabled ? 'on' : 'off'}</span></td>
                <td className="row">
                  <button className="link" onClick={() => { setEditing(r); setForm({ name: r.name, eventType: r.eventType, activityType: r.activityType ?? null, kind: r.kind, amount: r.amount, enabled: r.enabled }); }}>Edit</button>
                  <button className="link" onClick={() => void toggle(r)}>{r.enabled ? 'Turn off' : 'Turn on'}</button>
                  <button className="link" onClick={() => void remove(r)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form onSubmit={saveRule} className="card stack" style={{ marginTop: '1rem' }}>
        <b>{editing ? `Edit “${editing.name}”` : 'New rule'}</b>
        <div className="row">
          <div style={{ flex: 2 }}><label>Name</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Poll vote" /></div>
          <div style={{ flex: 1 }}><label>Event</label>
            <select value={form.eventType} onChange={(e) => setForm({ ...form, eventType: e.target.value, kind: 'fixed' })}>{EVENTS.map((ev) => <option key={ev}>{ev}</option>)}</select>
          </div>
          {form.eventType === 'participation.recorded' && (
            <div style={{ flex: 1 }}><label>Activity</label>
              <select value={form.activityType ?? ''} onChange={(e) => setForm({ ...form, activityType: e.target.value || null, kind: e.target.value === 'quiz' ? form.kind : 'fixed' })}>
                <option value="">any</option><option value="poll">poll</option><option value="quiz">quiz</option>
              </select>
            </div>
          )}
          <div style={{ flex: 1 }}><label>Kind</label>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              <option value="fixed">fixed amount</option>
              {form.activityType === 'quiz' && <option value="per_correct">per correct answer</option>}
              {form.activityType === 'quiz' && <option value="from_result">the quiz's own points per question</option>}
            </select>
          </div>
          <div style={{ width: 110 }}><label>Points</label><input type="number" required value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} /></div>
        </div>
        <div className="row">
          <button className="primary">{editing ? 'Save' : 'Add rule'}</button>
          {editing && <button type="button" onClick={() => { setEditing(null); setForm(blank); }}>Cancel</button>}
        </div>
      </form>

      <h2>Tier ladder</h2>
      <p className="muted">Levels by points. The first starts at 0; a fan's tier is the highest threshold they have reached.</p>
      <form onSubmit={saveLadder} className="card stack">
        <div className="options">
          {ladder.map((t, i) => (
            <div className="option" key={i} style={{ gridTemplateColumns: '2fr 1fr auto' }}>
              <input required value={t.name} onChange={(e) => setLadder(ladder.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} placeholder="Tier name" />
              <input type="number" min={0} required value={t.minPoints} onChange={(e) => setLadder(ladder.map((x, j) => (j === i ? { ...x, minPoints: Number(e.target.value) } : x)))} />
              <button type="button" className="link" onClick={() => setLadder(ladder.filter((_, j) => j !== i))}>Remove</button>
            </div>
          ))}
        </div>
        <div className="row">
          <button type="button" className="link" onClick={() => setLadder([...ladder, { name: '', minPoints: (ladder[ladder.length - 1]?.minPoints ?? 0) + 500 }])}>+ Add tier</button>
          <button className="primary">Save ladder</button>
        </div>
      </form>
    </>
  );
}
